import type { ReportRow } from '@/store/slices/reportingV2/reportingV2.api';
import { Box, Chip, Tooltip, Typography } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  dimensionLabel,
  geoIgnoredFilterNote,
  isConfirmedNonUsCountry,
  isPlaceholderDimension,
  maxOf,
  num,
  ratio,
  sharePercent,
  sumOf,
} from '../format';
// Re-exported for GeographicPerformance.test.ts and any other existing
// importer — the implementation itself moved to format.ts (flank#4880) so
// ImpressionsByType's Geo tab can share it instead of duplicating it.
export { GEO_IGNORED_FILTER_LABELS, geoIgnoredFilterNote } from '../format';
import { useExportWidgetCsv } from '../hooks/useExportWidgetCsv';
import { useReportWidget } from '../hooks/useReportWidget';
import { COLORS, cardSx } from '../theme';
import CityChoropleth from './CityChoropleth';
import DmaChoropleth from './DmaChoropleth';
import StateChoropleth from './StateChoropleth';
import WidgetExportButton from './WidgetExportButton';
import WidgetSkeleton from './WidgetSkeleton';
import ZipChoropleth from './ZipChoropleth';
import type { FocusRequest } from './mapViewport';

const WIDGET_KEY = 'geoPerf';

/**
 * The top-15 ranked bar list. Its own component (not inlined) so ZIP and City
 * can render it alongside their choropleths (review, CodeRabbit): a ZIP shape
 * conveys nothing without a label, and City's boundaries can fail to resolve
 * independently of the data — either way the list stays the fallback the
 * state/market choropleths don't need.
 */
const RankedGeoList = ({
  rows,
  dim,
  max,
  stateKey,
  onRowClick,
}: {
  rows: Record<string, unknown>[];
  dim: string;
  max: number;
  /** City shares its name across states (two Springfields, etc.) — when set, appends the row's state so rows stay distinguishable (review, Lucas). */
  stateKey?: string;
  /** flank#4755 — only passed for City/ZIP (the two mapped granularities with
   * a choropleth stacked above this list); every other granularity's rows
   * stay plain, non-interactive text, same as before. */
  onRowClick?: (row: Record<string, unknown>) => void;
}) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
    {rows.slice(0, 15).map((row, index) => {
      const label = row[dim];
      const state = stateKey ? row[stateKey] : undefined;
      const display = state ? `${dimensionLabel(label)}, ${state}` : dimensionLabel(label);
      const activate = () => onRowClick?.(row);
      return (
        <Box
          key={`${display}-${index}`}
          role={onRowClick ? 'button' : undefined}
          tabIndex={onRowClick ? 0 : undefined}
          onClick={onRowClick ? activate : undefined}
          onKeyDown={
            onRowClick
              ? e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    activate();
                  }
                }
              : undefined
          }
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
            ...(onRowClick
              ? {
                  cursor: 'pointer',
                  borderRadius: 1,
                  mx: -0.75,
                  px: 0.75,
                  '&:hover': { bgcolor: COLORS.pageBg },
                  '&:focus-visible': { outline: `2px solid ${COLORS.primary}`, outlineOffset: 2 },
                }
              : {}),
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
            <Typography
              variant='body2'
              title={onRowClick ? `Zoom the map to ${display}` : display}
              sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            >
              {display}
            </Typography>
            <Typography variant='body2' sx={{ fontWeight: 600 }}>
              {num(row.impressions)}
            </Typography>
          </Box>
          <Box sx={{ height: 6, borderRadius: 3, bgcolor: COLORS.border }}>
            <Box
              sx={{
                height: 6,
                borderRadius: 3,
                bgcolor: COLORS.primary,
                width: `${ratio(row.impressions, max) * 100}%`,
              }}
            />
          </Box>
        </Box>
      );
    })}
  </Box>
);

/**
 * Share of the total that has to be hidden before the card discloses it. Matches
 * BreakdownCard's threshold and reasoning: below 1% the hidden slice is stray
 * nulls and the note is more alarming than the gap, while a concentrated miss is
 * systematic whatever its share.
 */
const HIDDEN_SHARE_THRESHOLD = 0.01;

// Selectable granularities mapped to their canonical geo dimension. County has
// no backing dimension yet, so it renders disabled (dim === null).
//
// Country, County, and Congressional are temporarily hidden from the selector
// (team decision, 2026-10 Unified Reporting readiness review): County and
// Congressional need zip-code mapping to work across DSPs, which doesn't
// exist yet, and Country was dropped alongside them pending that work. The
// dims below are kept (not deleted) so the underlying data plumbing survives
// for the zip-code-based reintroduction — only HIDDEN_GRANULARITIES below
// hides them from the chip row.
export const GRANULARITIES: { label: string; dim: string | null }[] = [
  { label: 'Country', dim: 'geo_country' },
  { label: 'State', dim: 'geo_state' },
  { label: 'Market', dim: 'geo_dma' },
  { label: 'County', dim: null },
  { label: 'Congressional', dim: 'geo_congressional_district' },
  { label: 'City', dim: 'geo_city' },
  // Labelled rather than raw: a bare ZIP made regional reporting unreadable to
  // anyone who didn't know the market (FB-006). `dim` here is this chip's
  // identity (ranked-list column, placeholder checks) — the actual query
  // below groups by raw `geo_zip` alone and reattaches this label via
  // mergeZipLabels (flank#4811, flank#4812).
  { label: 'ZIP', dim: 'geo_zip_label' },
];

/**
 * Reattaches a friendly ZIP label onto the value query's rows (flank#4811,
 * flank#4812). The value query groups by raw `geo_zip` alone so each row IS
 * the real per-ZIP total and the 100-row page cap lands on 100 real ZIPs —
 * grouping by `geo_zip_label` instead (the old behaviour) splits any ZIP with
 * more than one historical city/state spelling across rows: one silently
 * overwrote another's value in ZipChoropleth, and the split rows shared the
 * same cap, so an advertiser with 100 real ZIPs could see far fewer on the map.
 *
 * `labelRows` comes from a second, decoupled query that groups by
 * `geo_zip_label` purely for display. Its own cap can occasionally miss a
 * long-tail ZIP's label — that ZIP falls back to its bare code below, which
 * never affects the real value above.
 */
export const mergeZipLabels = (rows: ReportRow[], labelRows: ReportRow[]): ReportRow[] => {
  const labelByZip = new Map<string, ReportRow['geo_zip_label']>();
  for (const row of labelRows) {
    const zip = String(row.geo_zip ?? '').trim();
    if (zip && !labelByZip.has(zip)) labelByZip.set(zip, row.geo_zip_label);
  }
  return rows.map(row => {
    const zip = String(row.geo_zip ?? '').trim();
    return { ...row, geo_zip_label: labelByZip.get(zip) ?? row.geo_zip };
  });
};

/**
 * flank#4813 added `geo_country` to City/ZIP's own group-by so a
 * positively-identified non-US row can be dropped before boundary-matching —
 * but that also splits a single real city/ZIP into two rows whenever its
 * delivery spans a confirmed-US row and a missing/unknown-country row for the
 * same place (both pass the filter above; only a POSITIVE non-US match is
 * dropped). CityChoropleth/ZipChoropleth already sum these by key
 * (`valueByKey`) before coloring the map — re-sum the same way here so the
 * ranked list agrees with it instead of showing the same place twice at a
 * fraction of its real total (review, CodeRabbit).
 */
export const aggregateRankedRows = (rows: ReportRow[], groupKey: (row: ReportRow) => string): ReportRow[] => {
  const byKey = new Map<string, ReportRow>();
  for (const row of rows) {
    const key = groupKey(row);
    const existing = byKey.get(key);
    if (existing) existing.impressions = Number(existing.impressions ?? 0) + Number(row.impressions ?? 0);
    else byKey.set(key, { ...row });
  }
  return Array.from(byKey.values()).sort((a, b) => Number(b.impressions ?? 0) - Number(a.impressions ?? 0));
};

const HIDDEN_GRANULARITIES = new Set(['Country', 'County', 'Congressional']);
export const VISIBLE_GRANULARITIES = GRANULARITIES.filter(option => !HIDDEN_GRANULARITIES.has(option.label));

// flank#4746 — "the map opens on State by default": a fixed literal, not
// derived from GRANULARITIES/VISIBLE_GRANULARITIES order, so hiding/reordering
// granularities (as HIDDEN_GRANULARITIES above does) can never change it.
export const DEFAULT_GRANULARITY = 'State';

/** Full-width geo card: granularity chips + ranked horizontal-bar list (top 15). */
const GeographicPerformance = () => {
  const [granularity, setGranularity] = useState(DEFAULT_GRANULARITY);
  // flank#4755 — the row last clicked in the City/ZIP ranked list, in each
  // choropleth's own feature-matching key shape (see CityChoropleth/
  // ZipChoropleth). Cleared on every granularity switch so a City zoom-in
  // doesn't carry over and silently no-op against the ZIP map underneath it.
  // `requestId` increments on every click, even a repeat click on the same
  // row, so the choropleth can re-focus it (React otherwise bails on an
  // unchanged key — review, CodeRabbit on #4777).
  const [focusRequest, setFocusRequest] = useState<FocusRequest | null>(null);
  const clickRequestIdRef = useRef(0);
  const requestFocus = (key: string) => {
    clickRequestIdRef.current += 1;
    setFocusRequest({ key, requestId: clickRequestIdRef.current });
  };

  const activeDim = GRANULARITIES.find(g => g.label === granularity)?.dim ?? 'geo_state';
  const isZip = activeDim === 'geo_zip_label';

  // A map needs every shape it can colour, not just the leaders: at limit 15 the
  // state map coloured 15 of 51 and a metro map would colour 15 of 206, leaving
  // most of the country looking like it had no delivery. Ranked lists still show
  // the top 15, so only the mapped granularities pay for the larger page.
  const isMapped = activeDim === 'geo_state' || activeDim === 'geo_dma' || isZip || activeDim === 'geo_city';
  const {
    rows: rawRows,
    totalRows,
    loading,
    error,
    ignoredFilters,
  } = useReportWidget(WIDGET_KEY, {
    // The DMA map matches boundaries on code. The ZIP map matches boundaries.io
    // ZIPs on the raw code too, and groups by that raw code alone (flank#4811,
    // flank#4812) — see mergeZipLabels for why `geo_zip_label` isn't requested
    // here. City has no usable code dimension in canonical, so CityChoropleth
    // resolves boundaries by name+state instead (same as Custom Dashboards).
    // ZIP and City also request `geo_country` (flank#4813) so ZipChoropleth/
    // CityChoropleth can drop a positively-identified non-US row before it
    // reaches boundary-matching — a non-US postcode/city otherwise risked
    // being drawn as the US one it happens to collide with.
    dimensions:
      activeDim === 'geo_dma'
        ? [activeDim, 'geo_dma_code']
        : isZip
          ? ['geo_zip', 'geo_country']
          : activeDim === 'geo_city'
            ? [activeDim, 'geo_state_code', 'geo_country']
            : [activeDim],
    // Reach/frequency are for the map tooltip; the ranked list below reads impressions only.
    metrics: ['impressions', 'reach', 'frequency'],
    sort: [{ field: 'impressions', dir: 'desc' }],
    // ZIP's own map/list only ever use 100/15 of a page (review, Lucas) — the
    // 300 other mapped granularities need is wasted Snowflake rows here.
    limit: isZip ? 100 : isMapped ? 300 : 15,
  });

  // A filter change re-fetches and unmounts/remounts the choropleth mid-flight
  // (review, CodeRabbit on #4777) — the remounted choropleth starts a fresh
  // click tracker while this component still holds the old `focusRequest`, so
  // the click-focus effect can apply a stale request over the new dataset's
  // auto-focus once loading finishes. Clear it the moment a refetch starts,
  // before the remount happens, rather than leaving it to be overridden later.
  useEffect(() => {
    if (loading) setFocusRequest(null);
  }, [loading]);

  // Decoupled, display-only query (see mergeZipLabels) — same 100-row budget as
  // the value query above since it only needs each real ZIP's dominant spelling,
  // never the ZIP itself.
  const { rows: zipLabelRows } = useReportWidget('geoPerfZipLabels', {
    dimensions: ['geo_zip', 'geo_zip_label'],
    metrics: ['impressions'],
    sort: [{ field: 'impressions', dir: 'desc' }],
    limit: 100,
    enabled: isZip,
  });

  const rows = useMemo(() => (isZip ? mergeZipLabels(rawRows, zipLabelRows) : rawRows), [isZip, rawRows, zipLabelRows]);
  // Same non-US exclusion as rankedRows/the choropleths below (flank#4813) —
  // otherwise the CSV disagrees with everything on screen by keeping rows the
  // map and list both dropped. Placeholders stay in: unlike the ranked list,
  // the export is a full data dump, not a top-15.
  const exportRows = useMemo(() => rows.filter(row => !isConfirmedNonUsCountry(row.geo_country)), [rows]);
  const onExportCsv = useExportWidgetCsv('geoPerf', exportRows);

  // Placeholder rows are the absence of a value, not a place, and they dwarf the
  // real ones — a 30-day ZIP list led with GAM's nulls (135M) and DV360's literal
  // "Unknown" (25M) above a top real ZIP of 801K. Rank the places; disclose the
  // rest below rather than letting them own the chart.
  // Also drop confirmed non-US rows (flank#4813), same rule the ZIP/City
  // choropleths apply — otherwise a colliding ZIP/city (e.g. a Mexican and a US
  // postcode sharing a code) shows twice here under the same US label, agreeing
  // with neither the map nor its own "no delivery" states. `geo_country` is only
  // requested for ZIP/City, so this is a no-op for State/Market.
  const rankedRows = useMemo(() => {
    const filtered = rows.filter(
      row => !isPlaceholderDimension(row[activeDim]) && !isConfirmedNonUsCountry(row.geo_country),
    );
    // Re-aggregate the City/ZIP split described above (review, CodeRabbit) —
    // see aggregateRankedRows.
    if (activeDim === 'geo_city') {
      return aggregateRankedRows(filtered, row => `${row[activeDim]}|${row.geo_state_code}`);
    }
    if (isZip) return aggregateRankedRows(filtered, row => String(row.geo_zip ?? ''));
    return filtered;
  }, [rows, activeDim, isZip]);
  const max = maxOf(rankedRows, 'impressions');
  const isEmpty = rows.length === 0;
  // Rows dropped specifically for being a confirmed non-US delivery — unlike a
  // placeholder row, these DO have a real city/ZIP attached, so they can't
  // share the "no {granularity} attached" explanation below (review,
  // CodeRabbit). Tracked separately from the generic placeholder/unresolved
  // bucket so both messages below say what's actually true of each.
  const excludedCountryTotal = sumOf(
    rows.filter(row => !isPlaceholderDimension(row[activeDim]) && isConfirmedNonUsCountry(row.geo_country)),
    'impressions',
  );
  // A dimension can exist in canonical and still be entirely NULL — congressional
  // district is today: one "Unmapped" row carrying every impression. Listing that
  // single row reads as a broken breakdown, so say what's actually going on.
  const allUnmapped = !isEmpty && rankedRows.length === 0 && excludedCountryTotal === 0;
  const allExcludedNonUs = !isEmpty && rankedRows.length === 0 && excludedCountryTotal > 0;

  // Hiding the placeholders silently would restate every bar against a smaller
  // base with nothing on screen saying so — the same failure BreakdownCard
  // documents. Disclose once the hidden slice stops being rounding noise.
  const grandTotal = sumOf(rows, 'impressions');
  const rankedTotal = sumOf(rankedRows, 'impressions');
  const hiddenImpressions = grandTotal - rankedTotal;
  const unmappedImpressions = hiddenImpressions - excludedCountryTotal;
  const hiddenShare = grandTotal > 0 ? hiddenImpressions / grandTotal : 0;
  const showHiddenNote = !allUnmapped && !allExcludedNonUs && hiddenShare >= HIDDEN_SHARE_THRESHOLD;
  // Two different reasons a row can be hidden, so two different clauses
  // (review, CodeRabbit) — unmapped deliveries have no {granularity} attached
  // at all; excluded-country deliveries have one, they're just non-US.
  const hiddenNoteText = [
    unmappedImpressions > 0
      ? `${num(unmappedImpressions)} (${sharePercent(unmappedImpressions, grandTotal)}) aren't counted here — some platforms don't report at this level, and some report a delivery they couldn't resolve. The delivery is real, it just has no ${granularity.toLowerCase()} attached.`
      : null,
    excludedCountryTotal > 0
      ? `${num(excludedCountryTotal)} (${sharePercent(excludedCountryTotal, grandTotal)}) have a non-US ${granularity.toLowerCase()} and are excluded from this view.`
      : null,
  ]
    .filter(Boolean)
    .join(' ');

  // flank#4880 — Device/Publisher filters are silently dropped for every
  // granularity here (GEOGRAPHY has neither column), not just the mapped
  // ones, so this isn't gated on `isMapped` or on the rows being empty: the
  // API still returns unfiltered rows, which otherwise look exactly like a
  // correctly-filtered result.
  const ignoredFilterNote = useMemo(() => geoIgnoredFilterNote(ignoredFilters), [ignoredFilters]);

  return (
    // rv2-map marks the choropleth for print: its canvas is ~4:1, and forcing
    // height to fill the print container squashed it (Earl's "map is squished").
    <Box className='rv2-card rv2-map' sx={{ ...cardSx, p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant='subtitle1' sx={{ fontWeight: 600, color: COLORS.text }}>
          Geographic Performance
        </Typography>
        <WidgetExportButton onExport={onExportCsv} disabled={rows.length === 0} />
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
        <Typography variant='body2' sx={{ color: COLORS.muted, mr: 0.5 }}>
          Granularity (per Platform capability):
        </Typography>
        {VISIBLE_GRANULARITIES.map(option => {
          const disabled = option.dim === null;
          const active = option.label === granularity;
          const chip = (
            <Chip
              key={option.label}
              label={option.label}
              size='small'
              disabled={disabled}
              variant={active ? 'filled' : 'outlined'}
              onClick={
                disabled
                  ? undefined
                  : () => {
                      setGranularity(option.label);
                      setFocusRequest(null);
                    }
              }
              sx={{
                fontWeight: 600,
                ...(active
                  ? {
                      bgcolor: COLORS.primary,
                      color: '#fff',
                      '&:hover': { bgcolor: COLORS.primaryHover },
                    }
                  : { borderColor: COLORS.border, color: COLORS.text }),
              }}
            />
          );
          // A greyed chip with no explanation reads as a bug. Say why, using the
          // same span-wrapped tooltip pattern as the disabled filter dropdowns.
          return disabled ? (
            <Tooltip
              key={option.label}
              title='County has no backing geo dimension in canonical yet.'
              placement='top'
              arrow
            >
              <span style={{ display: 'inline-flex' }}>{chip}</span>
            </Tooltip>
          ) : (
            chip
          );
        })}
      </Box>

      {!loading && !error && ignoredFilterNote ? (
        <Typography variant='body2' sx={{ color: COLORS.muted, fontSize: 13 }}>
          {ignoredFilterNote}
        </Typography>
      ) : null}

      {loading ? (
        <WidgetSkeleton height={360} />
      ) : error ? (
        <Typography variant='body2' color='error'>
          {error}
        </Typography>
      ) : isEmpty ? (
        <Typography variant='body2' color='text.secondary'>
          No regional data for the selected filters.
        </Typography>
      ) : allUnmapped ? (
        <Typography variant='body2' sx={{ color: COLORS.muted, py: 4, textAlign: 'center' }}>
          {`${granularity} isn't populated in canonical yet — every impression in this window is unattributed at this level.`}
        </Typography>
      ) : allExcludedNonUs ? (
        <Typography variant='body2' sx={{ color: COLORS.muted, py: 4, textAlign: 'center' }}>
          {`Every impression in this window has a non-US ${granularity.toLowerCase()} and is excluded from this view.`}
        </Typography>
      ) : activeDim === 'geo_state' ? (
        <StateChoropleth rows={rows} labelKey='geo_state' valueKey='impressions' />
      ) : activeDim === 'geo_dma' ? (
        <DmaChoropleth rows={rows} labelKey='geo_dma' valueKey='impressions' />
      ) : activeDim === 'geo_city' ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <CityChoropleth
            rows={rows}
            totalRows={totalRows}
            labelKey='geo_city'
            valueKey='impressions'
            stateKey='geo_state_code'
            focusRequest={focusRequest}
          />
          <RankedGeoList
            rows={rankedRows}
            dim={activeDim}
            max={max}
            stateKey='geo_state_code'
            onRowClick={row =>
              requestFocus(`${dimensionLabel(row[activeDim]).trim()}, ${String(row.geo_state_code ?? '').trim()}`)
            }
          />
        </Box>
      ) : activeDim === 'geo_zip_label' ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <ZipChoropleth
            rows={rows}
            totalRows={totalRows}
            labelKey='geo_zip'
            valueKey='impressions'
            focusRequest={focusRequest}
          />
          <RankedGeoList
            rows={rankedRows}
            dim={activeDim}
            max={max}
            onRowClick={row => requestFocus(String(row.geo_zip ?? '').trim())}
          />
        </Box>
      ) : (
        <RankedGeoList rows={rankedRows} dim={activeDim} max={max} />
      )}

      {showHiddenNote ? (
        <Typography variant='body2' sx={{ color: COLORS.muted, fontSize: 13 }}>
          {`Ranked over the ${sharePercent(rankedTotal, grandTotal)} of impressions that carry a ${granularity.toLowerCase()}. ${hiddenNoteText}`}
        </Typography>
      ) : null}
    </Box>
  );
};

export default GeographicPerformance;
